import { ModelError_Item } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { upperFirst } from 'lodash-es';

import { StampDateFieldName, StampTimeFieldName } from './StampDateTimeField';

interface StampFieldConfig {
  apiField: string;
  dateName: StampDateFieldName;
  isReadOnly?: boolean;
  labelKey: string;
  timeName: StampTimeFieldName;
}

// order matters twice over: it is the drawer's render order — the first stamp with
// a recorded time is the anchor whose date defaults onto later stamps that gain a
// time but have no date (see stampDateLogic.ts) — and it is the chronological order
// the backend enforces between the stamps (TRE-14009). load_completed_at is
// system-set (isReadOnly) and never carries a validation error itself.
export const STAMP_FIELDS: StampFieldConfig[] = [
  {
    apiField: 'arrived_pickup_at',
    dateName: 'arrivedPickupDate',
    labelKey: 'loads.arrived_at_pickup',
    timeName: 'arrivedPickupTime',
  },
  {
    apiField: 'loaded_at',
    dateName: 'loadedDate',
    labelKey: 'loads.loaded',
    timeName: 'loadedTime',
  },
  {
    apiField: 'arrived_dropoff_at',
    dateName: 'arrivedDropoffDate',
    labelKey: 'loads.arrived_at_dropoff',
    timeName: 'arrivedDropoffTime',
  },
  {
    apiField: 'unloaded_at',
    dateName: 'unloadedDate',
    labelKey: 'loads.unloaded',
    timeName: 'unloadedTime',
  },
  {
    apiField: 'load_completed_at',
    dateName: 'loadCompletedDate',
    isReadOnly: true,
    labelKey: 'loads.load_completed',
    timeName: 'loadCompletedTime',
  },
];

export interface StampFieldError {
  field: StampTimeFieldName;
  message: string;
}

// rails builds validation messages as "<humanized attribute> <message>", so a stamp
// field surfaces in two shapes: humanized at the start ("arrived_pickup_at" →
// "Arrived pickup at ...") and raw when interpolated as the ordering bound
// ("... must be less than or equal to loaded_at"). swap both for the display label
// exactly as the form renders it, so the message reads "Arrived at Pickup must be
// less than or equal to Loaded".
function humanizeStampErrorMessage(erroredField: StampFieldConfig, message: string) {
  const railsHumanizedField = upperFirst(erroredField.apiField.replace(/_/g, ' '));

  let humanizedMessage = message;

  if (humanizedMessage.startsWith(railsHumanizedField)) {
    humanizedMessage =
      t(erroredField.labelKey) + humanizedMessage.slice(railsHumanizedField.length);
  }

  STAMP_FIELDS.forEach(({ apiField, labelKey }) => {
    // \b keeps loaded_at from matching inside unloaded_at
    humanizedMessage = humanizedMessage.replace(
      new RegExp(`\\b${apiField}\\b`, 'g'),
      t(labelKey),
    );
  });

  return upperFirst(humanizedMessage);
}

export function mapLoadSummaryErrorsToStampFields(errors: ModelError_Item[]) {
  return errors.reduce<StampFieldError[]>((stampErrors, { field, message }) => {
    const stampField = STAMP_FIELDS.find(
      (config) => config.apiField === field && !config.isReadOnly,
    );

    if (stampField) {
      stampErrors.push({
        field: stampField.timeName,
        message: humanizeStampErrorMessage(stampField, message),
      });
    }

    return stampErrors;
  }, []);
}
